import { useEffect, useState } from "react";
import {
  Plus,
  RefreshCcw,
  Trash2,
  Webhook,
  Loader2,
  Copy,
  Check,
  CircleCheck,
  CircleX,
  Clock3,
} from "lucide-react";
import {
  createWebhook,
  deleteWebhook,
  friendlyAuthError,
  listWebhookDeliveries,
  listWebhooks,
  updateWebhook,
  type WebhookDelivery,
  type WebhookRow,
} from "@/lib/api";

const SUPPORTED_EVENTS = [
  "*",
  "secret.created",
  "secret.updated",
  "secret.deleted",
  "secret.restored",
  "approval.requested",
  "approval.approved",
  "approval.rejected",
  "api_key.created",
  "api_key.revoked",
];

export function WebhooksSection({ orgId }: { orgId: string }) {
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["*"]);
  const [createdSecret, setCreatedSecret] = useState<{ id: string; secret: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Record<string, WebhookDelivery[]>>({});

  async function load() {
    setLoading(true);
    try {
      const list = await listWebhooks(orgId);
      setHooks(list);
      setErr(null);
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await createWebhook(orgId, { name: name.trim(), url: url.trim(), events });
      setCreatedSecret({ id: r.webhook.id, secret: r.signing_secret });
      setName("");
      setUrl("");
      setEvents(["*"]);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(h: WebhookRow) {
    setBusy(h.id);
    try {
      await updateWebhook(orgId, h.id, { active: !h.active });
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this webhook? Pending deliveries will be cancelled.")) return;
    setBusy(id);
    try {
      await deleteWebhook(orgId, id);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  async function loadDeliveries(id: string) {
    try {
      const list = await listWebhookDeliveries(orgId, id);
      setDeliveries((prev) => ({ ...prev, [id]: list }));
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="space-y-5">
      <div
        className="rounded-2xl bg-[var(--color-surface)] p-5"
        style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
      >
        <div className="mb-3 flex items-center gap-2">
          <Webhook className="h-4 w-4 text-[var(--color-accent)]" />
          <h3 className="text-sm font-semibold text-[var(--color-text)]">Add webhook</h3>
        </div>
        <form onSubmit={create} className="grid gap-3 md:grid-cols-2">
          <input
            className="field-input md:col-span-1"
            placeholder="Name (e.g. PagerDuty)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            maxLength={80}
          />
          <input
            className="field-input md:col-span-1"
            placeholder="https://example.com/hooks/dataly"
            type="url"
            pattern="https://.*"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
          <div className="md:col-span-2">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-subtle)]">
              Events
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SUPPORTED_EVENTS.map((ev) => {
                const on = events.includes(ev);
                return (
                  <button
                    key={ev}
                    type="button"
                    onClick={() => {
                      if (ev === "*") {
                        setEvents(["*"]);
                        return;
                      }
                      let next = on
                        ? events.filter((e2) => e2 !== ev)
                        : [...events.filter((e2) => e2 !== "*"), ev];
                      if (next.length === 0) next = ["*"];
                      setEvents(next);
                    }}
                    className={
                      "rounded-full border px-3 py-1 text-[11px] transition " +
                      (on
                        ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]"
                        : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)]")
                    }
                  >
                    {ev}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={creating || !name.trim() || !/^https:\/\//i.test(url)}
              className="btn btn-primary py-2 text-sm"
            >
              {creating ? <Loader2 className="h-4 w-4 spin" /> : <Plus className="h-4 w-4" />}
              Create webhook
            </button>
          </div>
        </form>
        {createdSecret && <CreatedSigningSecret secret={createdSecret.secret} />}
      </div>

      <div
        className="rounded-2xl bg-[var(--color-surface)] p-5"
        style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">Webhooks</h3>
          <button
            type="button"
            onClick={load}
            className="rounded-lg border border-[var(--color-border)] p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)]"
            aria-label="Refresh"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
        {err && (
          <div className="mb-3 flex items-start justify-between gap-3 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-xs text-[var(--color-danger)]">
            <span>{err}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="shrink-0 rounded-md border border-[rgba(255,59,48,0.35)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-danger)] hover:bg-[rgba(255,59,48,0.08)]"
            >
              Retry
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 spin text-[var(--color-text-muted)]" />
          </div>
        ) : err ? null : hooks.length === 0 ? (
          <p className="rounded-lg bg-[var(--color-surface-2)] px-3 py-6 text-center text-xs text-[var(--color-text-muted)]">
            No webhooks configured yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {hooks.map((h) => {
              const open = expanded === h.id;
              return (
                <li
                  key={h.id}
                  className="rounded-xl bg-[var(--color-surface-2)] p-3 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-1.5 font-medium text-[var(--color-text)]">
                        {h.name}
                        {h.active ? (
                          <span className="rounded-full bg-[var(--color-accent-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-accent-hover)]">
                            Active
                          </span>
                        ) : (
                          <span className="rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)]">
                            Paused
                          </span>
                        )}
                        {h.last_status != null && (
                          <span
                            className={
                              "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                              (h.last_status >= 200 && h.last_status < 300
                                ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
                                : "bg-[var(--color-danger-bg)] text-[var(--color-danger)]")
                            }
                          >
                            {h.last_status >= 200 && h.last_status < 300 ? (
                              <CircleCheck className="h-3 w-3" />
                            ) : (
                              <CircleX className="h-3 w-3" />
                            )}
                            HTTP {h.last_status}
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 truncate text-[var(--color-text-muted)]">
                        {h.url}
                      </p>
                      <p className="mt-0.5 text-[10px] text-[var(--color-text-subtle)]">
                        Events: {h.events.join(", ")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleActive(h)}
                      disabled={busy === h.id}
                      className="rounded-lg border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-surface-3)]"
                    >
                      {h.active ? "Pause" : "Resume"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setExpanded(open ? null : h.id);
                        if (!open && !deliveries[h.id]) loadDeliveries(h.id);
                      }}
                      className="rounded-lg border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-surface-3)]"
                    >
                      {open ? "Hide" : "Deliveries"}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(h.id)}
                      disabled={busy === h.id}
                      className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-surface-3)]"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  </div>
                  {open && (
                    <DeliveryTable rows={deliveries[h.id] ?? null} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function DeliveryTable({ rows }: { rows: WebhookDelivery[] | null }) {
  if (rows === null) {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-[var(--color-text-muted)]">
        <Loader2 className="h-3 w-3 spin" />
        Loading deliveries…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="mt-3 rounded-lg bg-[var(--color-surface)] px-3 py-2 text-center text-[10px] text-[var(--color-text-muted)]">
        No deliveries yet.
      </p>
    );
  }
  return (
    <div className="mt-3 overflow-hidden rounded-lg bg-[var(--color-surface)]">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="bg-[var(--color-surface-3)] text-[var(--color-text-subtle)]">
            <th className="px-2 py-1 text-left font-semibold">Created</th>
            <th className="px-2 py-1 text-left font-semibold">Event</th>
            <th className="px-2 py-1 text-left font-semibold">Attempts</th>
            <th className="px-2 py-1 text-left font-semibold">Status</th>
            <th className="px-2 py-1 text-left font-semibold">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} className="border-t border-[var(--color-border)]">
              <td className="px-2 py-1 text-[var(--color-text-muted)]">
                {new Date(d.created_at).toLocaleString()}
              </td>
              <td className="px-2 py-1 text-[var(--color-text)]">{d.event}</td>
              <td className="px-2 py-1 text-[var(--color-text-muted)]">{d.attempts}</td>
              <td className="px-2 py-1 text-[var(--color-text-muted)]">{d.last_status ?? "—"}</td>
              <td className="px-2 py-1">
                {d.delivered_at ? (
                  <span className="flex items-center gap-1 text-[var(--color-success)]">
                    <CircleCheck className="h-3 w-3" />
                    Delivered
                  </span>
                ) : d.failed_at ? (
                  <span className="flex items-center gap-1 text-[var(--color-danger)]">
                    <CircleX className="h-3 w-3" />
                    Failed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[var(--color-text-muted)]">
                    <Clock3 className="h-3 w-3" />
                    Retrying
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreatedSigningSecret({ secret }: { secret: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="mt-4 rounded-lg border-2 border-dashed border-[var(--color-accent)] bg-[var(--color-accent-soft)] p-3 text-xs"
    >
      <p className="font-semibold text-[var(--color-accent-hover)]">
        Webhook signing secret — shown once
      </p>
      <p className="mt-1 text-[var(--color-text-muted)]">
        Used in the <code>x-dataly-signature</code> header (HMAC-SHA256 over <code>{"{ts}.{body}"}</code>).
      </p>
      <div className="mt-2 flex items-center gap-2 rounded-lg bg-[var(--color-surface)] px-3 py-2">
        <code className="flex-1 break-all font-mono text-[11px]">{secret}</code>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(secret);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              /* ignore */
            }
          }}
          className="rounded-md p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)]"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
